import { stripMarkdown } from '@littlebird/markdown-utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import tunnel from 'tunnel-rat';

import { useSearchFocusState } from '@/hooks/useSearchFocusState';
import { searchItems, useVirtualListFind } from '@/hooks/useVirtualListFind';
import { toCaseInsensitiveSearchText } from '@/lib/unicode-search';

import type { ChatItem } from './chatItems.types';
import type { Message } from '@littlebird/sdk';
import type { VListHandle } from 'virtua';

export const chatFindTunnel = tunnel();

const SEARCH_FOCUS_HIGHLIGHT_MS = 1000;
const MAX_SEARCH_FOCUS_RETRIES = 2;

interface FocusRetryState {
  focusKey?: string;
  count: number;
}

interface FocusAttemptControllerOptions {
  focusKey: string;
  retryToken: number;
  appliedFocusKeyRef: { current: string | undefined };
  scheduledFocusKeyRef: { current: string | undefined };
  retryState: FocusRetryState;
  maxRetries: number;
  onAcknowledgeFocus: () => void;
  onRetry: () => void;
  onNotFound: () => void;
}

interface FocusAttemptController {
  isAlreadyApplied: boolean;
  hasInFlightAttempt: boolean;
  startAttempt: (suffix?: string) => string;
  clearAttemptIfCurrent: (attemptKey: string) => void;
  createResultHandler: (
    attemptKey: string,
    shouldRetryOnFailure?: () => boolean
  ) => (didFocus: boolean) => void;
}

function createFocusAttemptController({
  focusKey,
  retryToken,
  appliedFocusKeyRef,
  scheduledFocusKeyRef,
  retryState,
  maxRetries,
  onAcknowledgeFocus,
  onRetry,
  onNotFound,
}: FocusAttemptControllerOptions): FocusAttemptController {
  if (retryState.focusKey !== focusKey) {
    retryState.focusKey = focusKey;
    retryState.count = 0;
  }

  const baseAttemptKey = `${focusKey}:${retryToken}`;

  const startAttempt = (suffix?: string): string => {
    const attemptKey = suffix ? `${baseAttemptKey}:${suffix}` : baseAttemptKey;
    scheduledFocusKeyRef.current = attemptKey;
    return attemptKey;
  };

  const clearAttemptIfCurrent = (attemptKey: string) => {
    if (scheduledFocusKeyRef.current !== attemptKey) return;
    scheduledFocusKeyRef.current = undefined;
  };

  const createResultHandler = (
    attemptKey: string,
    shouldRetryOnFailure: () => boolean = () => true
  ) => {
    return (didFocus: boolean) => {
      if (scheduledFocusKeyRef.current !== attemptKey) return;

      if (didFocus) {
        scheduledFocusKeyRef.current = undefined;
        retryState.count = 0;
        onAcknowledgeFocus();
        return;
      }

      scheduledFocusKeyRef.current = undefined;

      if (!shouldRetryOnFailure()) return;

      const nextRetryCount = retryState.count + 1;
      retryState.count = nextRetryCount;

      if (nextRetryCount <= maxRetries) {
        onRetry();
        return;
      }

      onNotFound();
    };
  };

  return {
    isAlreadyApplied: appliedFocusKeyRef.current === focusKey,
    hasInFlightAttempt:
      scheduledFocusKeyRef.current?.startsWith(baseAttemptKey) ?? false,
    startAttempt,
    clearAttemptIfCurrent,
    createResultHandler,
  };
}

interface UseChatFindOptions {
  messages: Message[];
  chatItems: ChatItem[];
  vlistRef: React.RefObject<VListHandle | null>;
  containerRef: React.RefObject<HTMLElement | null>;
  threadId: string | undefined;
}

export function useChatFind({
  messages,
  chatItems,
  vlistRef,
  containerRef,
  threadId,
}: UseChatFindOptions) {
  const searchMap = useMemo(() => {
    const map: { msgIndex: number; content: string }[] = [];
    for (let i = 0; i < messages.length; i++) {
      const content = stripMarkdown(messages[i].content ?? '');
      if (content) map.push({ msgIndex: i, content });
    }
    return map;
  }, [messages]);

  const normalizedSearchMap = useMemo(
    () => searchMap.map((item) => toCaseInsensitiveSearchText(item.content)),
    [searchMap]
  );

  // Ref avoids making `search` depend on chatItems (which rebuilds every render)
  const chatItemsRef = useRef(chatItems);
  chatItemsRef.current = chatItems;

  // Re-run search when searchable item mapping changes (e.g. older messages load)
  const searchIndexSignature = useMemo(() => {
    let signature = '';
    for (let i = 0; i < chatItems.length; i++) {
      const item = chatItems[i];
      if (item.type === 'userMessage' || item.type === 'assistantResponse') {
        signature += `m:${item.index}@${i}|`;
      } else if (item.type === 'dateSeparator') {
        signature += `d:${item.id}@${i}|`;
      }
    }
    return signature;
  }, [chatItems]);

  const search = useCallback(
    (query: string, caseSensitive: boolean) => {
      const currentChatItems = chatItemsRef.current;
      const msgToVList = new Map<number, number>();
      for (let i = 0; i < currentChatItems.length; i++) {
        const item = currentChatItems[i];
        if (item.type === 'userMessage' || item.type === 'assistantResponse') {
          msgToVList.set(item.index, i);
        }
      }

      const messageResults = searchItems(
        {
          length: searchMap.length,
          getText: (i) => searchMap[i].content,
          getNormalizedText: (i) => normalizedSearchMap[i] ?? '',
        },
        query,
        caseSensitive
      )
        .map((result) => {
          const vlistIndex = msgToVList.get(
            searchMap[result.itemIndex].msgIndex
          );
          if (vlistIndex === undefined) return null;
          return { ...result, itemIndex: vlistIndex };
        })
        .filter((r) => r !== null);

      const dateItems: { vlistIndex: number; content: string }[] = [];
      for (let i = 0; i < currentChatItems.length; i++) {
        const item = currentChatItems[i];
        if (item.type === 'dateSeparator') {
          dateItems.push({ vlistIndex: i, content: item.date });
        }
      }

      const dateResults = searchItems(
        { length: dateItems.length, getText: (i) => dateItems[i].content },
        query,
        caseSensitive
      ).map((result) => ({
        ...result,
        itemIndex: dateItems[result.itemIndex].vlistIndex,
      }));

      return [...messageResults, ...dateResults].sort(
        (a, b) =>
          a.itemIndex - b.itemIndex || a.occurrenceIndex - b.occurrenceIndex
      );
    },
    [searchMap, normalizedSearchMap]
  );

  const find = useVirtualListFind({
    vlistRef,
    containerRef,
    search,
    itemIdPrefix: 'chat-item',
  });

  const { isOpen, close, focus, handleScroll, query, setQuery } = find;

  const repaintHighlightsRef = useRef(handleScroll);
  repaintHighlightsRef.current = handleScroll;

  const repaintHighlights = useCallback(
    () => repaintHighlightsRef.current(),
    []
  );

  const { searchFocusTarget, onSearchFocus } = useSearchFocus(threadId);

  const appliedFocusKeyRef = useRef<string | undefined>(undefined);
  const scheduledFocusKeyRef = useRef<string | undefined>(undefined);
  const focusRetryStateRef = useRef<FocusRetryState>({
    focusKey: undefined,
    count: 0,
  });
  const hasMountedThreadResetRef = useRef(false);
  const [focusRetryToken, setFocusRetryToken] = useState(0);

  useEffect(() => {
    if (!searchFocusTarget?.focusKey) {
      appliedFocusKeyRef.current = undefined;
      scheduledFocusKeyRef.current = undefined;
      focusRetryStateRef.current = {
        focusKey: undefined,
        count: 0,
      };
    }
  }, [searchFocusTarget?.focusKey]);

  useEffect(() => {
    if (!isOpen || !query) return;
    setQuery(query);
  }, [isOpen, query, setQuery, searchIndexSignature]);

  const hasPendingSearchFocus =
    Boolean(searchFocusTarget?.query) &&
    appliedFocusKeyRef.current !== searchFocusTarget?.focusKey;

  const handleFocusNotFound = useCallback(() => {
    scheduledFocusKeyRef.current = undefined;
    focusRetryStateRef.current.count = 0;
    onSearchFocus?.();
  }, [onSearchFocus]);

  const focusSearchTarget = useCallback(() => {
    if (!searchFocusTarget?.query) return false;

    const focusKey = searchFocusTarget.focusKey;

    const attemptController = createFocusAttemptController({
      focusKey,
      retryToken: focusRetryToken,
      appliedFocusKeyRef,
      scheduledFocusKeyRef,
      retryState: focusRetryStateRef.current,
      maxRetries: MAX_SEARCH_FOCUS_RETRIES,
      onAcknowledgeFocus: () => {
        appliedFocusKeyRef.current = focusKey;
        onSearchFocus?.();
      },
      onRetry: () => {
        setFocusRetryToken((value) => value + 1);
      },
      onNotFound: handleFocusNotFound,
    });

    if (attemptController.isAlreadyApplied) return false;
    if (attemptController.hasInFlightAttempt) return false;

    const requestedOccurrence = Math.max(searchFocusTarget.occurrence ?? 0, 0);

    if (searchFocusTarget.bucketId) {
      const bucketMessageIndex = messages.findIndex(
        (message) => message.id === searchFocusTarget.bucketId
      );

      if (bucketMessageIndex === -1) {
        handleFocusNotFound();
        return false;
      }

      const targetVListIndex = chatItemsRef.current.findIndex(
        (item) =>
          (item.type === 'userMessage' || item.type === 'assistantResponse') &&
          item.index === bucketMessageIndex
      );

      if (targetVListIndex === -1) {
        handleFocusNotFound();
        return false;
      }

      let shouldRetryBucketFailure = false;
      const bucketAttemptKey = attemptController.startAttempt();

      const focusedBucket = focus({
        query: searchFocusTarget.query,
        itemIndex: targetVListIndex,
        occurrence: requestedOccurrence,
        caseSensitive: false,
        highlightMode: 'current',
        clearAfterMs: SEARCH_FOCUS_HIGHLIGHT_MS,
        onResult: attemptController.createResultHandler(
          bucketAttemptKey,
          () => shouldRetryBucketFailure
        ),
      });

      shouldRetryBucketFailure = focusedBucket;

      if (focusedBucket) return true;

      const fallbackAttemptKey = attemptController.startAttempt('fallback');

      const fallbackFocused = focus({
        query: searchFocusTarget.query,
        occurrence: requestedOccurrence,
        caseSensitive: false,
        highlightMode: 'current',
        clearAfterMs: SEARCH_FOCUS_HIGHLIGHT_MS,
        onResult: attemptController.createResultHandler(fallbackAttemptKey),
      });
      if (!fallbackFocused) {
        attemptController.clearAttemptIfCurrent(fallbackAttemptKey);
      }

      return fallbackFocused;
    }

    const attemptKey = attemptController.startAttempt();

    const focused = focus({
      query: searchFocusTarget.query,
      occurrence: requestedOccurrence,
      caseSensitive: false,
      highlightMode: 'current',
      clearAfterMs: SEARCH_FOCUS_HIGHLIGHT_MS,
      onResult: attemptController.createResultHandler(attemptKey),
    });
    if (!focused) {
      attemptController.clearAttemptIfCurrent(attemptKey);
    }

    return focused;
  }, [
    focus,
    focusRetryToken,
    handleFocusNotFound,
    messages,
    onSearchFocus,
    searchFocusTarget,
  ]);

  useEffect(() => {
    if (!hasMountedThreadResetRef.current) {
      hasMountedThreadResetRef.current = true;
      return;
    }

    scheduledFocusKeyRef.current = undefined;
    focusRetryStateRef.current = {
      focusKey: undefined,
      count: 0,
    };
    close();
  }, [close, threadId]);

  return {
    ...find,
    repaintHighlights,
    hasPendingSearchFocus,
    focusSearchTarget,
  };
}

interface SearchFocusTarget {
  focusKey: string;
  query: string;
  bucketId?: string;
  occurrence?: number;
}

interface UseSearchFocusResult {
  searchFocusTarget?: SearchFocusTarget;
  onSearchFocus: () => void;
}

function useSearchFocus(threadId?: string): UseSearchFocusResult {
  const { clearSearchFocusParams, searchFocus } = useSearchFocusState(
    threadId ? { threadId } : undefined
  );

  const searchFocusTarget = useMemo<SearchFocusTarget | undefined>(() => {
    if (!searchFocus.focusQuery) {
      return undefined;
    }

    const occurrence = searchFocus.focusOccurrence ?? 0;

    return {
      focusKey: `${threadId ?? '0'}:${searchFocus.focusQuery}:${searchFocus.focusBucketId ?? '0'}:${occurrence}`,
      query: searchFocus.focusQuery,
      bucketId: searchFocus.focusBucketId,
      occurrence,
    };
  }, [
    searchFocus.focusBucketId,
    searchFocus.focusOccurrence,
    searchFocus.focusQuery,
    threadId,
  ]);

  const onSearchFocus = clearSearchFocusParams;

  return {
    onSearchFocus,
    searchFocusTarget,
  };
}
